import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import withApiError, {
  ERROR_TYPE_REPORTING,
} from 'layers/errorHandling/apiError'
import { selectors as licenseSelectors } from 'store/licenseManager'
import { selectors as reportsSelectors } from 'store/reportsManager'
import NavigationHelper from 'layers/navigation/navigationHelper'
import SchoolSummary from './component'
import {
  selectSchoolSummaryReportTableData,
  UNIT_RANGE_OPTIONS,
} from './selectors'
import {
  DEFAULT_K8_REPORT_UNIT_SECTION,
  K8_REPORT_UNIT_SECTION,
} from 'pages/LessonCompletionReport/flag.on/constants'
import { createDefaultActivitySelection } from './activityFilterConstants'
import { createDefaultGradeSelection } from './gradeFilterConstants'
import {
  ACTIVITY_SLUG_TO_NAME_MAP,
  GRADE_SLUG_TO_NAME_MAP,
  UNIT_RANGE_SLUG_TO_NAME_MAP,
} from './slugToNameMap'

export const SchoolSummaryContainer = ({
  administratorsCount,
  classDetailsReport,
  entry,
  getReportTableData,
  gradesWithClasses,
  gradesWithNoClasses,
  hasClassesWithCompletedLessonLastPeriod,
  isFetchingSchool,
  k8ReportUnitSection,
  lastUpdatedAt,
  navigationHandler,
  numClassesWithCompletedLessonLastPeriod,
  period,
  schoolEmptyDisplay,
  selectedClassInstance,
  selectedSite,
  siteName,
  teachersCount,
  totalClassesCount,
}) => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const activityParams = searchParams.get('activity')
  const gradesParams = searchParams.get('grades')
  const unitParam = searchParams.get('unit')

  const [selectedUnitRange, setSelectedUnitRange] = useState(
    UNIT_RANGE_OPTIONS.CORE,
  )
  const [selectedActivityKeys, setSelectedActivityKeys] = useState(
    createDefaultActivitySelection,
  )
  const [selectedGradeNames, setSelectedGradeNames] = useState(
    createDefaultGradeSelection,
  )

  useEffect(() => {
    if (k8ReportUnitSection === K8_REPORT_UNIT_SECTION.SPECIALIZED_UNITS) {
      setSelectedUnitRange(UNIT_RANGE_OPTIONS.CORE)
    }
  }, [k8ReportUnitSection])

  useEffect(() => {
    if (gradesParams?.length > 0 && !selectedGradeNames.size) {
      setGradesParamsToSelectedGradeNames(gradesParams)
    }
    if (activityParams?.length > 0 && !selectedActivityKeys.size) {
      setActivityParamsToSelectedActivityKeys(activityParams)
    }
    if (unitParam?.length > 0) {
      const unitRangeName = UNIT_RANGE_SLUG_TO_NAME_MAP?.[unitParam]
      setSelectedUnitRange(unitRangeName || UNIT_RANGE_OPTIONS.CORE)
    }
  }, [selectedActivityKeys, selectedGradeNames, selectedUnitRange])

  const handleSelectGrade = (navigationFunction, gradeName) => {
    navigationFunction({
      gradeName,
      siteId: selectedSite,
    })
  }

  const setGradesParamsToSelectedGradeNames = grades => {
    const gradeNames = grades
      .split(',')
      .map(grade => grade.toLowerCase().trim())
      .filter(Boolean)
      .map(grade => GRADE_SLUG_TO_NAME_MAP?.[grade])
    setSelectedGradeNames(new Set(gradeNames))
  }

  const setActivityParamsToSelectedActivityKeys = activities => {
    const activityKeys = activities
      .split(',')
      .map(activity => activity.toLowerCase().trim())
      .filter(Boolean)
      .map(activity => ACTIVITY_SLUG_TO_NAME_MAP?.[activity])
    setSelectedActivityKeys(new Set(activityKeys))
  }

  const handleUnitRangeChange = useCallback(
    unitRange => {
      setSelectedUnitRange(prev => {
        const next = unitRange || prev
        navigationHandler(
          {
            activity: [...selectedActivityKeys],
            grades: [...selectedGradeNames],
            siteId: selectedSite,
            unit: next,
          },
          NavigationHelper.types.REPLACE,
        )
        return next
      })
    },
    [selectedActivityKeys, selectedGradeNames, selectedSite, selectedUnitRange],
  )

  const toggleGradeFilter = useCallback(
    gradeName => {
      setSelectedGradeNames(prev => {
        const next = new Set(prev)
        if (next.has(gradeName)) next.delete(gradeName)
        else next.add(gradeName)
        navigationHandler(
          {
            activity: [...selectedActivityKeys],
            grades: [...next],
            siteId: selectedSite,
            unit: selectedUnitRange,
          },
          NavigationHelper.types.REPLACE,
        )
        return next
      })
    },
    [selectedActivityKeys, selectedGradeNames, selectedSite, selectedUnitRange],
  )

  const toggleActivityFilter = useCallback(
    activityLabel => {
      setSelectedActivityKeys(prev => {
        const next = new Set(prev)
        if (next.has(activityLabel)) next.delete(activityLabel)
        else next.add(activityLabel)
        navigationHandler(
          {
            activity: [...next],
            grades: [...selectedGradeNames],
            siteId: selectedSite,
            unit: selectedUnitRange,
          },
          NavigationHelper.types.REPLACE,
        )
        return next
      })
    },
    [selectedActivityKeys, selectedGradeNames, selectedSite, selectedUnitRange],
  )

  const resetFilters = useCallback(() => {
    setSelectedGradeNames(createDefaultGradeSelection())
    setSelectedActivityKeys(createDefaultActivitySelection())
    navigationHandler({ siteId: selectedSite }, NavigationHelper.types.REPLACE)
  }, [navigationHandler, selectedSite])

  useEffect(() => {
    setSelectedGradeNames(createDefaultGradeSelection())
    setSelectedActivityKeys(createDefaultActivitySelection())
  }, [k8ReportUnitSection])

  const reportTableData = getReportTableData(selectedUnitRange)

  return (
    <SchoolSummary
      administratorsCount={administratorsCount}
      classDetailsReport={classDetailsReport}
      entry={entry}
      gradesWithClasses={gradesWithClasses}
      gradesWithNoClasses={gradesWithNoClasses}
      handleSelectGrade={handleSelectGrade}
      handleUnitRangeChange={handleUnitRangeChange}
      hasClassesWithCompletedLessonLastPeriod={
        hasClassesWithCompletedLessonLastPeriod
      }
      isFetchingSchool={isFetchingSchool}
      k8ReportUnitSection={k8ReportUnitSection}
      lastUpdatedAt={lastUpdatedAt}
      navigationHandler={navigationHandler}
      numClassesWithCompletedLessonLastPeriod={
        numClassesWithCompletedLessonLastPeriod
      }
      period={period}
      reportTableData={reportTableData}
      resetFilters={resetFilters}
      schoolEmptyDisplay={schoolEmptyDisplay}
      selectedActivityKeys={selectedActivityKeys}
      selectedClassInstance={selectedClassInstance}
      selectedGradeNames={selectedGradeNames}
      selectedSite={selectedSite}
      selectedUnitRange={selectedUnitRange}
      siteName={siteName}
      teachersCount={teachersCount}
      toggleActivityFilter={toggleActivityFilter}
      toggleGradeFilter={toggleGradeFilter}
      totalClassesCount={totalClassesCount}
    />
  )
}

export const mapStateToProps = (state, ownProps) => {
  const { reportsManager, router } = state
  const { isFetchingSchool, selectedSite } = reportsManager || {}
  const activeAdminSitePreferences = licenseSelectors.selectActiveAdminSitePreferences(
    state,
  )
  const activeSitePreferences = activeAdminSitePreferences?.[selectedSite]
  const rbhuIsEnabled =
    activeSitePreferences?.k5Digital?.bullyingHarassmentUnitEnabled ||
    activeSitePreferences?.mspDigital?.bullyingHarassmentUnitEnabled
  const k8ReportUnitSection =
    ownProps.k8ReportUnitSection ?? DEFAULT_K8_REPORT_UNIT_SECTION
  const getReportTableData = selectedUnitRange =>
    selectSchoolSummaryReportTableData(
      state,
      selectedUnitRange,
      k8ReportUnitSection,
      rbhuIsEnabled,
    )
  const schoolSummaryReport = reportsSelectors.selectSchoolSummaryReport(state)
  const {
    administratorsCount,
    gradesWithClasses,
    gradesWithNoClasses,
    hasClassesWithCompletedLessonLastPeriod,
    lastUpdatedAt,
    numClassesWithCompletedLessonLastPeriod,
    period,
    siteName,
    teachersCount,
    totalClassesCount,
  } = schoolSummaryReport || {}

  const selectedClassInstance = router?.location?.query?.class || ''
  const classDetailsReport = reportsSelectors.selectClassDetailsReport(
    state,
    selectedClassInstance,
  )

  return {
    administratorsCount,
    classDetailsReport,
    getReportTableData,
    gradesWithClasses,
    gradesWithNoClasses,
    hasClassesWithCompletedLessonLastPeriod,
    isFetchingSchool,
    lastUpdatedAt,
    numClassesWithCompletedLessonLastPeriod,
    period,
    selectedClassInstance,
    selectedSite,
    siteName,
    teachersCount,
    totalClassesCount,
  }
}

SchoolSummaryContainer.propTypes = {
  administratorsCount: PropTypes.number,
  classDetailsReport: PropTypes.object,
  entry: PropTypes.object,
  getReportTableData: PropTypes.func,
  gradesWithClasses: PropTypes.array,
  gradesWithNoClasses: PropTypes.array,
  hasClassesWithCompletedLessonLastPeriod: PropTypes.bool,
  isFetchingSchool: PropTypes.bool,
  k8ReportUnitSection: PropTypes.string,
  lastUpdatedAt: PropTypes.string,
  navigationHandler: PropTypes.func,
  numClassesWithCompletedLessonLastPeriod: PropTypes.number,
  period: PropTypes.number,
  schoolEmptyDisplay: PropTypes.object,
  selectedClassInstance: PropTypes.string,
  selectedSite: PropTypes.number,
  siteName: PropTypes.string,
  summaryReportExists: PropTypes.bool,
  summaryReportIsFetching: PropTypes.bool,
  teachersCount: PropTypes.number,
  totalClassesCount: PropTypes.number,
}

export default connect(mapStateToProps)(
  withApiError(SchoolSummaryContainer, [ERROR_TYPE_REPORTING]),
)
